import {
  assignmentId,
  applySchedule,
  buildProjectIndex,
  calendarId,
  customFieldId,
  dependencyId,
  id,
  money,
  percent,
  projectId,
  ratePerMinute,
  resolveCalendar,
  resourceId,
  taskId,
  minutes,
  schedule,
  withWbsCodes,
  type Assignment,
  type Calendar,
  type CustomFieldDefinition,
  type ISODate,
  type ISODateTime,
  type Project,
  type Resource,
  type Task,
  type TimephasedSet,
} from '@bigint-project/core';

/** A deliberately separate, portable JSON format; it contains no desktop view state. */
export const PROJECT_TEMPLATE_KIND = 'bigint-project-template' as const;
export const PROJECT_TEMPLATE_FORMAT_VERSION = 1 as const;

/** Planning copies remove execution history; snapshots preserve it verbatim. */
export type ProjectTemplateCaptureMode = 'Planning' | 'Snapshot';

export interface ProjectTemplateMetadata {
  readonly name: string;
  readonly description?: string;
  /** Informational UTC audit timestamp; it is not a Project wall-clock date. */
  readonly capturedAt: string;
  readonly sourceProjectId: string;
}

export interface ProjectTemplate {
  readonly kind: typeof PROJECT_TEMPLATE_KIND;
  readonly formatVersion: typeof PROJECT_TEMPLATE_FORMAT_VERSION;
  readonly metadata: ProjectTemplateMetadata;
  readonly captureMode: ProjectTemplateCaptureMode;
  readonly project: Project;
}

export interface CaptureProjectTemplateOptions {
  readonly captureMode: ProjectTemplateCaptureMode;
  readonly metadata?: Partial<Omit<ProjectTemplateMetadata, 'sourceProjectId'>>;
  /** Explicitly replace every resource rate and rate table with zeroes. */
  readonly removeResourceRates?: boolean;
  /** Explicitly replace every task fixed cost with zero. */
  readonly removeFixedCosts?: boolean;
}

export type TemplateEntityKind =
  | 'project'
  | 'task'
  | 'resource'
  | 'assignment'
  | 'dependency'
  | 'calendar'
  | 'calendar-exception'
  | 'custom-field'
  | 'budget-assignment'
  | 'timesheet'
  | 'timesheet-entry';

/** Injection keeps ID generation deterministic in tests and host-controlled in callers. */
export type ProjectTemplateIdFactory = (
  kind: TemplateEntityKind,
  sourceId: string,
  ordinal: number,
) => string;

export interface InstantiateProjectTemplateOptions {
  readonly projectName?: string;
  /**
   * New project scheduling anchor. It is supported only by Planning templates;
   * every project wall-clock/civil date is translated by the same civil delta.
   */
  readonly anchorDate?: ISODateTime;
  readonly idFactory?: ProjectTemplateIdFactory;
}

export class ProjectTemplateError extends Error {
  constructor(message: string, override readonly cause?: unknown) {
    super(message);
    this.name = 'ProjectTemplateError';
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asProject = (value: unknown): Project => value as Project;

/** Project domain data is JSON-shaped, but structuredClone also preserves absent/undefined optionals. */
function cloneProject(project: Project): Project {
  return structuredClone(project);
}

function assertProjectShape(value: unknown): asserts value is Project {
  if (!isPlainObject(value)) throw new ProjectTemplateError('template project must be an object');
  const candidate = asProject(value);
  if (
    typeof candidate.id !== 'string' ||
    typeof candidate.name !== 'string' ||
    typeof candidate.startDate !== 'string' ||
    typeof candidate.finishDate !== 'string' ||
    (candidate.scheduleFrom !== 'ProjectStart' && candidate.scheduleFrom !== 'ProjectFinish') ||
    !Array.isArray(candidate.tasks) ||
    !Array.isArray(candidate.dependencies) ||
    !Array.isArray(candidate.resources) ||
    !Array.isArray(candidate.assignments) ||
    !Array.isArray(candidate.calendars) ||
    !Array.isArray(candidate.baselines) ||
    !Array.isArray(candidate.customFields) ||
    (candidate.budgetAssignments !== undefined && !Array.isArray(candidate.budgetAssignments)) ||
    (candidate.timesheets !== undefined && !Array.isArray(candidate.timesheets))
  ) {
    throw new ProjectTemplateError('template project has incomplete structure');
  }
}

function validateProject(project: Project): void {
  try {
    const index = buildProjectIndex(project);
    if (!index.calendarsById.has(project.defaultCalendarId as string)) {
      throw new ProjectTemplateError(`default calendar is missing: ${project.defaultCalendarId as string}`);
    }
    // buildProjectIndex validates graph/reference identities; calendar resolution is
    // the separate core authority for inheritance cycles, exceptions and work times.
    for (const calendar of project.calendars) resolveCalendar(calendar, index.calendarsById);
    // Validate every stored civil date, including optional timephased/history data,
    // without ever using a host-local timezone or changing the project.
    const anchor = project.scheduleFrom === 'ProjectStart' ? project.startDate : project.finishDate;
    rebaseProject(project, anchor);
  } catch (error) {
    if (error instanceof ProjectTemplateError) throw error;
    throw new ProjectTemplateError(
      `template project is invalid: ${error instanceof Error ? error.message : String(error)}`,
      error,
    );
  }
}

function reschedulePlanningProject(project: Project): Project {
  try {
    return applySchedule(project, schedule({ project }));
  } catch (error) {
    throw new ProjectTemplateError(
      `Planning template cannot be scheduled: ${error instanceof Error ? error.message : String(error)}`,
      error,
    );
  }
}

function normaliseMetadata(project: Project, partial: Partial<Omit<ProjectTemplateMetadata, 'sourceProjectId'>> | undefined): ProjectTemplateMetadata {
  const name = partial?.name?.trim() || project.name.trim() || 'Untitled Template';
  return {
    name,
    ...(partial?.description?.trim() ? { description: partial.description.trim() } : {}),
    capturedAt: partial?.capturedAt ?? new Date().toISOString(),
    sourceProjectId: project.id as string,
  };
}

function withoutResourceRates(resource: Resource): Resource {
  const { rateTables: _rateTables, ...rest } = resource;
  return {
    ...rest,
    standardRate: ratePerMinute(0),
    overtimeRate: ratePerMinute(0),
    costPerUse: money(0),
  };
}

/** Idempotent sanitisation prevents a handcrafted Planning envelope from carrying execution state. */
function planningCopy(project: Project, options: Pick<CaptureProjectTemplateOptions, 'removeResourceRates' | 'removeFixedCosts'> = {}): Project {
  const tasks = project.tasks.map((task) => ({
    ...task,
    percentComplete: percent(0),
    percentWorkComplete: percent(0),
    actualStart: null,
    actualFinish: null,
    fixedCost: options.removeFixedCosts ? money(0) : task.fixedCost,
  }));
  const assignments = project.assignments.map((assignment) => {
    const { overtimeWork: _overtimeWork, levelingDelay: _levelingDelay, ...rest } = assignment;
    return {
      ...rest,
      actualWork: minutes(0),
      remainingWork: assignment.work,
      actualCost: money(0),
      remainingCost: assignment.cost,
      timephased: null,
    } satisfies Assignment;
  });
  return {
    ...project,
    statusDate: null,
    tasks,
    resources: options.removeResourceRates ? project.resources.map(withoutResourceRates) : project.resources,
    assignments,
    baselines: [],
    timesheets: [],
  };
}

/** Capture is non-mutating. Snapshot is lossless unless a caller explicitly elects a rate/cost scrub. */
export function captureProjectTemplate(project: Project, options: CaptureProjectTemplateOptions): ProjectTemplate {
  assertProjectShape(project);
  validateProject(project);
  if (options.captureMode !== 'Planning' && options.captureMode !== 'Snapshot') {
    throw new ProjectTemplateError(`unsupported capture mode: ${String(options.captureMode)}`);
  }
  const cloned = cloneProject(project);
  const rawCaptured = options.captureMode === 'Planning'
    ? planningCopy(cloned, options)
    : {
        ...cloned,
        tasks: options.removeFixedCosts ? cloned.tasks.map((task) => ({ ...task, fixedCost: money(0) })) : cloned.tasks,
        resources: options.removeResourceRates ? cloned.resources.map(withoutResourceRates) : cloned.resources,
      };
  const captured = options.captureMode === 'Planning' ? reschedulePlanningProject(rawCaptured) : rawCaptured;
  validateProject(captured);
  return {
    kind: PROJECT_TEMPLATE_KIND,
    formatVersion: PROJECT_TEMPLATE_FORMAT_VERSION,
    metadata: normaliseMetadata(project, options.metadata),
    captureMode: options.captureMode,
    project: captured,
  };
}

function assertTemplateShape(value: unknown): asserts value is ProjectTemplate {
  if (!isPlainObject(value)) throw new ProjectTemplateError('template root must be an object');
  if (value.kind !== PROJECT_TEMPLATE_KIND) throw new ProjectTemplateError(`unsupported template kind: ${String(value.kind)}`);
  if (value.formatVersion !== PROJECT_TEMPLATE_FORMAT_VERSION) {
    throw new ProjectTemplateError(`unsupported template version: ${String(value.formatVersion)}`);
  }
  if (value.captureMode !== 'Planning' && value.captureMode !== 'Snapshot') {
    throw new ProjectTemplateError(`unsupported capture mode: ${String(value.captureMode)}`);
  }
  if (!isPlainObject(value.metadata) || typeof value.metadata.name !== 'string' || typeof value.metadata.capturedAt !== 'string' || typeof value.metadata.sourceProjectId !== 'string') {
    throw new ProjectTemplateError('template metadata is incomplete');
  }
  assertProjectShape(value.project);
}

function validateTemplate(template: ProjectTemplate): void {
  assertTemplateShape(template);
  validateProject(template.project);
}

export function serializeProjectTemplate(template: ProjectTemplate): string {
  validateTemplate(template);
  return JSON.stringify(template, null, 2);
}

export function deserializeProjectTemplate(json: string): ProjectTemplate {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    throw new ProjectTemplateError(`cannot parse template JSON: ${error instanceof Error ? error.message : String(error)}`, error);
  }
  assertTemplateShape(parsed);
  const template = parsed as ProjectTemplate;
  validateProject(template.project);
  return template;
}

function assertUniqueIds(entities: readonly { readonly id: string }[], label: string): void {
  const seen = new Set<string>();
  for (const entity of entities) {
    if (typeof entity.id !== 'string' || entity.id.length === 0) throw new ProjectTemplateError(`${label} has an empty id`);
    if (seen.has(entity.id)) throw new ProjectTemplateError(`duplicate ${label} id: ${entity.id}`);
    seen.add(entity.id);
  }
}

function assertUniqueUids(entities: readonly { readonly uid: number }[], label: string): void {
  const seen = new Set<number>();
  for (const entity of entities) {
    if (!Number.isInteger(entity.uid) || entity.uid < 0) throw new ProjectTemplateError(`${label} has invalid uid: ${String(entity.uid)}`);
    if (seen.has(entity.uid)) throw new ProjectTemplateError(`duplicate ${label} uid: ${String(entity.uid)}`);
    seen.add(entity.uid);
  }
}

interface RekeyMaps {
  readonly task: ReadonlyMap<string, string>;
  readonly resource: ReadonlyMap<string, string>;
  readonly assignment: ReadonlyMap<string, string>;
  readonly dependency: ReadonlyMap<string, string>;
  readonly calendar: ReadonlyMap<string, string>;
  readonly customField: ReadonlyMap<string, string>;
  readonly budgetAssignment: ReadonlyMap<string, string>;
  readonly timesheet: ReadonlyMap<string, string>;
}

function prepareSource(project: Project): void {
  assertUniqueIds(project.tasks, 'task');
  assertUniqueIds(project.resources, 'resource');
  assertUniqueIds(project.assignments, 'assignment');
  assertUniqueIds(project.dependencies, 'dependency');
  assertUniqueIds(project.calendars, 'calendar');
  assertUniqueIds(project.customFields, 'custom field');
  assertUniqueIds(project.budgetAssignments ?? [], 'budget assignment');
  assertUniqueIds(project.timesheets ?? [], 'timesheet');
  assertUniqueUids(project.tasks, 'task');
  assertUniqueUids(project.resources, 'resource');
}

function defaultIdFactory(kind: TemplateEntityKind, sourceId: string, ordinal: number): string {
  // Including the source makes repeated template-of-template instantiation
  // fresh without relying on host randomness or a local mutable counter.
  return `template-${kind}-${sourceId}-${String(ordinal)}`;
}

/** Enforce the host factory contract once for every generated ID, including non-referenced leaf IDs. */
function uniqueIdFactory(factory: ProjectTemplateIdFactory): ProjectTemplateIdFactory {
  const used = new Map<TemplateEntityKind, Set<string>>();
  return (kind, sourceId, ordinal) => {
    const generated = factory(kind, sourceId, ordinal);
    if (typeof generated !== 'string' || generated.trim().length === 0) {
      throw new ProjectTemplateError(`id factory returned an invalid ${kind} id`);
    }
    if (generated === sourceId) throw new ProjectTemplateError(`id factory must rekey ${kind} id: ${sourceId}`);
    const bucket = used.get(kind) ?? new Set<string>();
    used.set(kind, bucket);
    if (bucket.has(generated)) throw new ProjectTemplateError(`id factory produced duplicate ${kind} id: ${generated}`);
    bucket.add(generated);
    return generated;
  };
}

function makeRekeyMaps(project: Project, factory: ProjectTemplateIdFactory): RekeyMaps {
  const mapEntities = <T extends { readonly id: string }>(kind: TemplateEntityKind, entities: readonly T[]): ReadonlyMap<string, string> => {
    const next = new Map<string, string>();
    for (let index = 0; index < entities.length; index += 1) {
      const source = entities[index]!;
      const generated = factory(kind, source.id, index + 1);
      next.set(source.id, generated);
    }
    return next;
  };
  return {
    task: mapEntities('task', project.tasks),
    resource: mapEntities('resource', project.resources),
    assignment: mapEntities('assignment', project.assignments),
    dependency: mapEntities('dependency', project.dependencies),
    calendar: mapEntities('calendar', project.calendars),
    customField: mapEntities('custom-field', project.customFields),
    budgetAssignment: mapEntities('budget-assignment', project.budgetAssignments ?? []),
    timesheet: mapEntities('timesheet', project.timesheets ?? []),
  };
}

function mapped(map: ReadonlyMap<string, string>, source: string, label: string): string {
  const value = map.get(source);
  if (value === undefined) throw new ProjectTemplateError(`${label} references missing source id: ${source}`);
  return value;
}

function rekeyValues(
  values: Readonly<Record<string, string | number | boolean | null>> | undefined,
  fields: ReadonlyMap<string, string>,
): Readonly<Record<string, string | number | boolean | null>> | undefined {
  if (values === undefined) return undefined;
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [mapped(fields, key, 'custom field value'), value]));
}

function rekeyCalendars(project: Project, maps: RekeyMaps, factory: ProjectTemplateIdFactory): readonly Calendar[] {
  return project.calendars.map((calendar, calendarIndex) => ({
    ...calendar,
    id: calendarId(mapped(maps.calendar, calendar.id as string, 'calendar')),
    baseCalendarId: calendar.baseCalendarId === null ? null : calendarId(mapped(maps.calendar, calendar.baseCalendarId as string, 'calendar base')),
    exceptions: calendar.exceptions.map((exception, exceptionIndex) => ({
      ...exception,
      id: factory('calendar-exception', exception.id, calendarIndex * 100000 + exceptionIndex + 1),
    })),
  }));
}

function rekeyTasks(project: Project, maps: RekeyMaps, firstUid: number): readonly Task[] {
  return project.tasks.map((task, index) => ({
    ...task,
    id: taskId(mapped(maps.task, task.id as string, 'task')),
    uid: firstUid + index,
    parentId: task.parentId === null ? null : taskId(mapped(maps.task, task.parentId as string, 'task parent')),
    calendarId: task.calendarId === null ? null : calendarId(mapped(maps.calendar, task.calendarId as string, 'task calendar')),
    customFieldValues: rekeyValues(task.customFieldValues, maps.customField),
  }));
}

function rekeyResources(project: Project, maps: RekeyMaps, firstUid: number): readonly Resource[] {
  return project.resources.map((resource, index) => ({
    ...resource,
    id: resourceId(mapped(maps.resource, resource.id as string, 'resource')),
    uid: firstUid + index,
    calendarId: resource.calendarId === null ? null : calendarId(mapped(maps.calendar, resource.calendarId as string, 'resource calendar')),
    customFieldValues: rekeyValues(resource.customFieldValues, maps.customField),
  }));
}

function rekeyAssignments(project: Project, maps: RekeyMaps): readonly Assignment[] {
  return project.assignments.map((assignment) => ({
    ...assignment,
    id: assignmentId(mapped(maps.assignment, assignment.id as string, 'assignment')),
    taskId: taskId(mapped(maps.task, assignment.taskId as string, 'assignment task')),
    resourceId: resourceId(mapped(maps.resource, assignment.resourceId as string, 'assignment resource')),
    customFieldValues: rekeyValues(assignment.customFieldValues, maps.customField),
  }));
}

function rekeyCustomFields(project: Project, maps: RekeyMaps): readonly CustomFieldDefinition[] {
  return project.customFields.map((field) => ({
    ...field,
    id: customFieldId(mapped(maps.customField, field.id as string, 'custom field')),
  }));
}

function rekeyProject(project: Project, factory: ProjectTemplateIdFactory): Project {
  prepareSource(project);
  const maps = makeRekeyMaps(project, factory);
  const firstUid = Math.max(0, ...project.tasks.map((task) => task.uid), ...project.resources.map((resource) => resource.uid)) + 1;
  const next: Project = {
    ...project,
    id: projectId(factory('project', project.id as string, 1)),
    defaultCalendarId: calendarId(mapped(maps.calendar, project.defaultCalendarId as string, 'default calendar')),
    calendars: rekeyCalendars(project, maps, factory),
    tasks: rekeyTasks(project, maps, firstUid),
    resources: rekeyResources(project, maps, firstUid + project.tasks.length),
    assignments: rekeyAssignments(project, maps),
    dependencies: project.dependencies.map((dependency) => ({
      ...dependency,
      id: dependencyId(mapped(maps.dependency, dependency.id as string, 'dependency')),
      predecessorId: taskId(mapped(maps.task, dependency.predecessorId as string, 'dependency predecessor')),
      successorId: taskId(mapped(maps.task, dependency.successorId as string, 'dependency successor')),
    })),
    customFields: rekeyCustomFields(project, maps),
    budgetAssignments: project.budgetAssignments?.map((budget) => ({
      ...budget,
      id: mapped(maps.budgetAssignment, budget.id, 'budget assignment'),
      resourceId: resourceId(mapped(maps.resource, budget.resourceId as string, 'budget resource')),
    })),
    baselines: project.baselines.map((baseline) => ({
      ...baseline,
      tasks: baseline.tasks.map((entry) => ({ ...entry, taskId: taskId(mapped(maps.task, entry.taskId as string, 'baseline task')) })),
      assignments: baseline.assignments.map((entry) => ({
        ...entry,
        assignmentId: assignmentId(mapped(maps.assignment, entry.assignmentId as string, 'baseline assignment')),
        taskId: taskId(mapped(maps.task, entry.taskId as string, 'baseline assignment task')),
      })),
      budgetAssignments: baseline.budgetAssignments?.map((entry) => ({
        ...entry,
        budgetAssignmentId: mapped(maps.budgetAssignment, entry.budgetAssignmentId, 'baseline budget assignment'),
        resourceId: resourceId(mapped(maps.resource, entry.resourceId as string, 'baseline budget resource')),
      })),
    })),
    timesheets: project.timesheets?.map((sheet, sheetIndex) => ({
      ...sheet,
      id: id<'Timesheet'>(mapped(maps.timesheet, sheet.id as string, 'timesheet')),
      resourceId: resourceId(mapped(maps.resource, sheet.resourceId as string, 'timesheet resource')),
      approvedBy: sheet.approvedBy === null ? null : resourceId(mapped(maps.resource, sheet.approvedBy as string, 'timesheet approver')),
      entries: sheet.entries.map((entry, entryIndex) => ({
        ...entry,
        id: factory('timesheet-entry', entry.id, sheetIndex * 100000 + entryIndex + 1),
        taskId: taskId(mapped(maps.task, entry.taskId as string, 'timesheet task')),
        assignmentId: entry.assignmentId === null ? null : assignmentId(mapped(maps.assignment, entry.assignmentId as string, 'timesheet assignment')),
      })),
    })),
  };
  return withWbsCodes(next);
}

interface CivilDelta {
  readonly minutes: number;
  /**
   * 錨點橫跨的日曆日數。民曆日欄位（日曆例外、資源可用區間、工時表週期、
   * Date 自訂欄位）沒有時刻，只能整日移動，因此跟著錨點跨越的日數走，而不是
   * 把分鐘差加到當天午夜上——位移不是整日時，午夜加上負的分鐘差會退過日界，
   * 使民曆日比 wall-clock 多退一天。
   */
  readonly days: number;
}

function parseCivilDate(value: string): { readonly year: number; readonly month: number; readonly day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) throw new ProjectTemplateError(`invalid civil date: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const stamp = Date.UTC(year, month - 1, day);
  const date = new Date(stamp);
  if (year < 1900 || year > 9999 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    throw new ProjectTemplateError(`invalid civil date: ${value}`);
  }
  return { year, month, day };
}

function parseCivilDateTime(value: string): { readonly stamp: number; readonly hasSeconds: boolean } {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (match === null) throw new ProjectTemplateError(`invalid local wall-clock date-time: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6] ?? '0');
  const stamp = Date.UTC(year, month - 1, day, hour, minute, second);
  const date = new Date(stamp);
  if (
    year < 1900 || year > 9999 || hour > 23 || minute > 59 || second > 59 ||
    date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day ||
    date.getUTCHours() !== hour || date.getUTCMinutes() !== minute || date.getUTCSeconds() !== second
  ) {
    throw new ProjectTemplateError(`invalid local wall-clock date-time: ${value}`);
  }
  return { stamp, hasSeconds: match[6] !== undefined };
}

const pad2 = (value: number): string => String(value).padStart(2, '0');

function shiftDateTime(value: ISODateTime, delta: CivilDelta): ISODateTime {
  const parsed = parseCivilDateTime(value as string);
  const date = new Date(parsed.stamp + delta.minutes * 60_000);
  const suffix = parsed.hasSeconds ? `:${pad2(date.getUTCSeconds())}` : '';
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}${suffix}` as ISODateTime;
}

function shiftDate(value: ISODate, delta: CivilDelta): ISODate {
  const parsed = parseCivilDate(value as string);
  const date = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day + delta.days));
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}` as ISODate;
}

function shiftTimephased(value: TimephasedSet | null, delta: CivilDelta): TimephasedSet | null {
  if (value === null) return null;
  const shiftWork = <T extends { readonly start: ISODateTime; readonly end: ISODateTime }>(series: readonly T[]): readonly T[] => series.map((slice) => ({ ...slice, start: shiftDateTime(slice.start, delta), end: shiftDateTime(slice.end, delta) }));
  return {
    ...value,
    work: shiftWork(value.work),
    actualWork: shiftWork(value.actualWork),
    remainingWork: shiftWork(value.remainingWork),
    ...(value.overtimeWork === undefined ? {} : { overtimeWork: shiftWork(value.overtimeWork) }),
    cost: shiftWork(value.cost),
    actualCost: shiftWork(value.actualCost),
    ...(value.baselineWork === undefined ? {} : { baselineWork: shiftWork(value.baselineWork) }),
    ...(value.baselineCost === undefined ? {} : { baselineCost: shiftWork(value.baselineCost) }),
  };
}

function shiftCustomFieldValue(value: string | number | boolean | null, field: CustomFieldDefinition, delta: CivilDelta): string | number | boolean | null {
  if (field.kind !== 'Date' || value === null) return value;
  if (typeof value !== 'string') throw new ProjectTemplateError(`Date custom field ${field.id as string} must contain a civil date string`);
  return shiftDate(value as ISODate, delta) as string;
}

function rebaseCustomFields(fields: readonly CustomFieldDefinition[], delta: CivilDelta): readonly CustomFieldDefinition[] {
  return fields.map((field) => ({
    ...field,
    defaultValue: shiftCustomFieldValue(field.defaultValue, field, delta),
    lookup: field.lookup?.map((item) => ({ ...item, value: shiftCustomFieldValue(item.value, field, delta) })) ?? null,
  }));
}

function rebaseCustomValues(
  values: Readonly<Record<string, string | number | boolean | null>> | undefined,
  fieldsById: ReadonlyMap<string, CustomFieldDefinition>,
  delta: CivilDelta,
): Readonly<Record<string, string | number | boolean | null>> | undefined {
  if (values === undefined) return undefined;
  return Object.fromEntries(Object.entries(values).map(([fieldId, value]) => {
    const field = fieldsById.get(fieldId);
    if (field === undefined) throw new ProjectTemplateError(`custom field value references missing definition: ${fieldId}`);
    return [fieldId, shiftCustomFieldValue(value, field, delta)];
  }));
}

/** 兩個 wall-clock 時刻之間跨越的日曆日數（只看日期部分，不受時刻影響）。 */
function civilDayDelta(from: ISODateTime, to: ISODateTime): number {
  const start = parseCivilDate((from as string).slice(0, 10));
  const end = parseCivilDate((to as string).slice(0, 10));
  return (Date.UTC(end.year, end.month - 1, end.day) - Date.UTC(start.year, start.month - 1, start.day)) / 86_400_000;
}

function rebaseProject(project: Project, anchorDate: ISODateTime): Project {
  const sourceAnchor = project.scheduleFrom === 'ProjectStart' ? project.startDate : project.finishDate;
  const delta: CivilDelta = {
    minutes: (parseCivilDateTime(anchorDate as string).stamp - parseCivilDateTime(sourceAnchor as string).stamp) / 60_000,
    days: civilDayDelta(sourceAnchor, anchorDate),
  };
  if (!Number.isInteger(delta.minutes)) throw new ProjectTemplateError('anchor translation must be an integral minute');
  const task = (entry: Task): Task => ({
    ...entry,
    start: shiftDateTime(entry.start, delta), finish: shiftDateTime(entry.finish, delta),
    earlyStart: shiftDateTime(entry.earlyStart, delta), earlyFinish: shiftDateTime(entry.earlyFinish, delta),
    lateStart: shiftDateTime(entry.lateStart, delta), lateFinish: shiftDateTime(entry.lateFinish, delta),
    constraintDate: entry.constraintDate === null ? null : shiftDateTime(entry.constraintDate, delta),
    deadline: entry.deadline === null ? null : shiftDateTime(entry.deadline, delta),
    actualStart: entry.actualStart === null ? null : shiftDateTime(entry.actualStart, delta),
    actualFinish: entry.actualFinish === null ? null : shiftDateTime(entry.actualFinish, delta),
  });
  const customFields = rebaseCustomFields(project.customFields, delta);
  const customFieldsById = new Map(customFields.map((field) => [field.id as string, field]));
  return {
    ...project,
    startDate: shiftDateTime(project.startDate, delta),
    finishDate: shiftDateTime(project.finishDate, delta),
    statusDate: project.statusDate === null ? null : shiftDateTime(project.statusDate, delta),
    calendars: project.calendars.map((calendar) => ({
      ...calendar,
      exceptions: calendar.exceptions.map((exception) => ({
        ...exception,
        from: shiftDate(exception.from, delta), to: shiftDate(exception.to, delta),
        recurrence: exception.recurrence === undefined ? undefined : {
          ...exception.recurrence,
          until: exception.recurrence.until === undefined ? undefined : shiftDate(exception.recurrence.until, delta),
        },
      })),
    })),
    tasks: project.tasks.map((entry) => ({ ...task(entry), customFieldValues: rebaseCustomValues(entry.customFieldValues, customFieldsById, delta) })),
    resources: project.resources.map((resource) => ({
      ...resource,
      availability: resource.availability.map((availability) => ({ ...availability, from: shiftDate(availability.from, delta), to: availability.to === null ? null : shiftDate(availability.to, delta) })),
      rateTables: resource.rateTables === undefined ? undefined : Object.fromEntries(Object.entries(resource.rateTables).map(([key, periods]) => [key, periods?.map((period) => ({ ...period, effectiveFrom: shiftDateTime(period.effectiveFrom, delta) }))])),
      customFieldValues: rebaseCustomValues(resource.customFieldValues, customFieldsById, delta),
    })),
    assignments: project.assignments.map((assignment) => ({ ...assignment, start: shiftDateTime(assignment.start, delta), finish: shiftDateTime(assignment.finish, delta), timephased: shiftTimephased(assignment.timephased, delta), customFieldValues: rebaseCustomValues(assignment.customFieldValues, customFieldsById, delta) })),
    baselines: project.baselines.map((baseline) => ({
      ...baseline,
      savedAt: shiftDateTime(baseline.savedAt, delta),
      tasks: baseline.tasks.map((entry) => ({ ...entry, start: shiftDateTime(entry.start, delta), finish: shiftDateTime(entry.finish, delta) })),
      assignments: baseline.assignments.map((entry) => ({ ...entry, start: shiftDateTime(entry.start, delta), finish: shiftDateTime(entry.finish, delta) })),
    })),
    timesheets: project.timesheets?.map((sheet) => ({
      ...sheet,
      periodStart: shiftDate(sheet.periodStart, delta), periodEnd: shiftDate(sheet.periodEnd, delta),
      submittedAt: sheet.submittedAt === null ? null : shiftDateTime(sheet.submittedAt, delta),
      approvedAt: sheet.approvedAt === null ? null : shiftDateTime(sheet.approvedAt, delta),
      entries: sheet.entries.map((entry) => ({ ...entry, date: shiftDate(entry.date, delta) })),
    })),
    customFields,
  };
}

/**
 * Reconstructs an independent project graph. Planning templates are rescheduled
 * here because their dates were rebased onto a new anchor and must be re-derived
 * from the calendar; Snapshot templates are never rescheduled, so their stored
 * dates survive verbatim.
 */
export function instantiateProjectTemplate(template: ProjectTemplate, options: InstantiateProjectTemplateOptions = {}): Project {
  validateTemplate(template);
  const source = template.captureMode === 'Planning' ? planningCopy(cloneProject(template.project)) : cloneProject(template.project);
  const sourceAnchor = source.scheduleFrom === 'ProjectStart' ? source.startDate : source.finishDate;
  if (template.captureMode === 'Snapshot' && options.anchorDate !== undefined && options.anchorDate !== sourceAnchor) {
    throw new ProjectTemplateError('Snapshot templates cannot change the scheduling anchor');
  }
  const factory = uniqueIdFactory(options.idFactory ?? defaultIdFactory);
  let result = rekeyProject(source, factory);
  if (options.anchorDate !== undefined && options.anchorDate !== sourceAnchor) result = rebaseProject(result, options.anchorDate);
  if (template.captureMode === 'Planning') result = reschedulePlanningProject(result);
  if (options.projectName !== undefined) {
    const name = options.projectName.trim();
    if (name.length === 0) throw new ProjectTemplateError('project name must not be empty');
    result = { ...result, name };
  }
  validateProject(result);
  return result;
}
